import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import SecuredImage from 'components/SecuredImage';
import React, { useContext, useEffect, useState } from 'react';

import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Slide,
  Slider,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

import { useQueryClient } from '@tanstack/react-query';
import { Alert, ChevronLeft, ChevronRight, Delete } from 'baseui/icon';
import { Skeleton } from 'baseui/skeleton';
import { Tag } from 'baseui/tag';
import { Banner, KIND } from 'components/Banner';
import Text from 'components/Text';
import { useParams } from 'react-router-dom';
import { useDictionary } from 'services/api/dictionary/queries';
import {
  DefectPhoto,
  Observation,
  ObservationDefect,
} from 'services/api/inspection/types';
import { useStitches } from 'theme';
import { format } from 'date-fns';
import { Button } from 'components/Button';
import { DeleteOutline } from '@mui/icons-material';
import { useSnackbar } from 'baseui/snackbar';

import { DEFECT_IMAGE_TYPE, UpdateImageParams } from './defectImageTypes';
import { parseDrawables } from './imageGeometry';
import {
  useDeleteDefectPhotoMutation,
  useRelatedInspections,
} from './mutations';
import InteractiveImage from './InteractiveImage';

const DeleteOutlineIcon = () => <DeleteOutline fontSize="small" />;

function getPhotoLabel(type: keyof typeof DEFECT_IMAGE_TYPE) {
  const LABELS: Record<keyof typeof DEFECT_IMAGE_TYPE, string> = {
    cloned: 'Photo taken during Previous Inspection',
    new: 'New Photo taken for Current Inspection',
    prev: '',
  };
  return LABELS[type];
}

export default function DefectsImageGallery({
  defect,
}: {
  defect: ObservationDefect;
  observation: Observation;
}) {
  const { css, theme } = useStitches();
  const [isOpen, setIsOpen] = useState(false);
  const images = defect.photos;
  const isCloned = !!defect.previousDefectNumber;
  const displayImageLink = images.length ? images[0].annotatedLink : '';

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          if (displayImageLink) {
            setIsOpen(true);
          }
        }}
      >
        <SecuredImage
          link={displayImageLink}
          className={css({
            width: theme.sizing.scale1600,
            height: theme.sizing.scale1600,
          })}
        />
      </button>
      <Modal
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        overrides={{
          Dialog: {
            style: {
              width: '100%',
              maxWidth: '96rem',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <ModalHeader>
          {!isCloned ? (
            <Tag
              closeable={false}
              overrides={{
                Text: {
                  style: {
                    maxWidth: 'unset',
                  },
                },
              }}
            >
              No Previous Inspection
            </Tag>
          ) : null}
        </ModalHeader>
        <ModalBody
          $style={{
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 0,
            marginTop: 0,
          }}
          className={css({
            overflow: 'hidden',
            layout: 'row',
            flex: 1,
          })}
        >
          <div className={css({ flex: 1, overflow: 'hidden', layout: 'row' })}>
            {images.length ? (
              <>
                <div className={css({ width: isCloned ? '50%' : '100%' })}>
                  <CurrentDefectImages defectUUID={defect.uuid} />
                </div>
                {isCloned && (
                  <div className={css({ width: '50%' })}>
                    <PreviousDefectImages
                      defectUUID={defect.previousDefectNumber}
                    />
                  </div>
                )}
              </>
            ) : (
              <div
                className={css({
                  layout: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                })}
              >
                <Text>Defect has no images</Text>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

function SlidePosition() {
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(
    carouselContext.state.currentSlide + 1,
  );
  useEffect(() => {
    function onChange() {
      setCurrentSlide(carouselContext.state.currentSlide + 1);
    }
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  return (
    <Text css={{ textAlign: 'center' }} weight="semibold">
      {currentSlide}/{carouselContext.state.totalSlides}
    </Text>
  );
}

function PreviousDefectImages({ defectUUID = '' }: { defectUUID?: string }) {
  const { css } = useStitches();
  const { inspectionId = '' } = useParams<{ inspectionId: string }>();
  const inspectionQuery = useRelatedInspections({
    currentInspectionId: inspectionId,
  });

  if (inspectionQuery.isError) {
    return (
      <Banner
        title="Error"
        kind={KIND.negative}
        artwork={{
          icon: ({ size }) => <Alert size={size} />,
        }}
      >
        Failed to get previous defect, refresh page ang try again.
      </Banner>
    );
  }

  if (inspectionQuery.data && !inspectionQuery.isRefetching) {
    const { previousInspection } = inspectionQuery.data;
    if (previousInspection === null) {
      return null;
    }
    const previousDefect = previousInspection?.observations
      .reduce(
        (all, observation) => [...all, ...observation.observationDefects],
        [] as ObservationDefect[],
      )
      .find((d) => d.uuid === defectUUID);

    const observation = previousInspection?.observations.find(
      (observation) => observation.uuid === previousDefect?.observationId,
    );

    if (!observation || !previousDefect) {
      return <Text>Observation not found</Text>;
    }

    return (
      <div className={css({ layout: 'stack', height: '$full' })}>
        <div className={css({ height: '50%' })}>
          <DefectImages
            images={previousDefect.photos.map((photo) => {
              return {
                ...photo,
                defectImageType: 'prev',
              };
            })}
          />
        </div>
        <div className={css({ flexBasis: '50%' })}>
          <DefectInformation
            defect={previousDefect}
            observation={observation}
            isPreviousDefect
          />
        </div>
      </div>
    );
  }

  return <Skeleton width="100%" height="100%" animation />;
}

function CurrentDefectImages({ defectUUID = '' }: { defectUUID?: string }) {
  const { css } = useStitches();
  const { inspectionId = '' } = useParams<{ inspectionId: string }>();
  const inspectionQuery = useRelatedInspections({
    currentInspectionId: inspectionId,
  });

  if (inspectionQuery.isError) {
    return (
      <Banner
        title="Error"
        kind={KIND.negative}
        artwork={{
          icon: ({ size }) => <Alert size={size} />,
        }}
      >
        Failed to get defect, refresh page ang try again.
      </Banner>
    );
  }

  if (inspectionQuery.data && !inspectionQuery.isFetching) {
    const relatedInspections = inspectionQuery.data;
    const currentDefect = relatedInspections.currentInspection.observations
      .reduce(
        (all, observation) => [...all, ...observation.observationDefects],
        [] as ObservationDefect[],
      )
      .find((defect) => defect.uuid === defectUUID);
    const observation = relatedInspections.currentInspection.observations.find(
      (observation) => observation.uuid === currentDefect?.observationId,
    );
    const previousDefect = relatedInspections.previousInspection?.observations
      .reduce(
        (all, observation) => [...all, ...observation.observationDefects],
        [] as ObservationDefect[],
      )
      .find((d) => d.uuid === currentDefect?.previousDefectNumber);

    if (currentDefect?.id === undefined || observation?.uuid === undefined) {
      return (
        <Banner
          artwork={{
            icon: ({ size }) => <Alert size={size} />,
          }}
        >
          Defect not found
        </Banner>
      );
    }
    const isClonedDefect = !!currentDefect.previousDefectNumber;
    const getPhotoType = (
      photo: DefectPhoto,
    ): keyof typeof DEFECT_IMAGE_TYPE => {
      const previousDefectPhotos = previousDefect?.photos;
      return previousDefectPhotos?.find(
        /**
         * We use the `name` property to compare since the photo `uuid`
         * changes when photos are cloned
         */
        (previousPhoto) => previousPhoto.name === photo.name,
      )
        ? DEFECT_IMAGE_TYPE.cloned
        : DEFECT_IMAGE_TYPE.new;
    };
    /**
     * NOTE:
     * if the defect opend by the user is the cloned defect
     * from the previous inspection we will not display its related photo since it will be the same
     * as the current photo/s
     */
    return (
      <div className={css({ layout: 'stack', height: '$full' })}>
        <div className={css({ height: '50%' })}>
          <DefectImages
            defect={currentDefect}
            images={[
              ...currentDefect.photos.map((photo) => ({
                ...photo,
                defectImageType: getPhotoType(photo),
              })),
            ]}
            editable
            previousInspectionDate={relatedInspections.previousInspection?.startDate}
          />
        </div>
        <div className={css({ flexBasis: '50%' })}>
          <DefectInformation defect={currentDefect} observation={observation} />
        </div>
      </div>
    );
  }

  return <Skeleton width="100%" height="100%" animation />;
}

function DefectImages({
  images,
  defect,
  editable,
  previousInspectionDate,
}: {
  editable?: boolean;
  images: Array<
    DefectPhoto & {
      defectImageType: keyof typeof DEFECT_IMAGE_TYPE;
    }
  >;
  defect?: ObservationDefect;
  previousInspectionDate?: Date;
}) {
  const { css } = useStitches();

  return (
    <CarouselProvider
      naturalSlideWidth={16}
      naturalSlideHeight={9}
      totalSlides={images.length}
      className={css({ flex: 1, layout: 'row', height: '$full' })}
      dragEnabled={false}
      isIntrinsicHeight
      touchEnabled={false}
    >
      <div
        className={css({
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          height: '100%',
        })}
      >
        <Slider
          className={css({ height: '$full' })}
          classNameTray={css({ height: '$full' })}
          classNameTrayWrap={css({ height: '$full', position: 'relative' })}
        >
          {images.map((image, index) => (
            <Slide index={index} key={image.uuid}>
              {(() => {
                const drawables = parseDrawables(image.drawables);
                return (
              <InteractiveImage
                rectangles={drawables.rectangles}
                arrows={drawables.arrows}
                params={{
                  observationId: defect ? defect.observationId : '',
                  defectId: image.observationDefectId,
                  photoId: image.uuid,
                }}
                editable={editable}
                image={image}
              >
                <SecuredImage
                  link={image.annotatedLink}
                  className={css({
                    objectFit: 'contain',
                    width: '$full',
                    height: '$full',
                  })}
                />
              </InteractiveImage>
                );
              })()}
              {!!defect && (
                <div
                  className={css({
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    pb: '$scale600',
                    pr: '$scale1600',
                  })}
                >
                  <DeleteImage
                    params={{
                      observationId: defect.observationId,
                      defectId: image.observationDefectId,
                      photoId: image.uuid,
                    }}
                  />
                </div>
              )}
              {image.defectImageType !== 'prev' && (
                <div
                  className={css({
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '$primary',
                    color: '$white',
                    p: '$scale100',
                  })}
                >
                  {getPhotoLabel(image.defectImageType)} -{' '}
                  {format(
                    new Date(
                      image.defectImageType === 'cloned' && previousInspectionDate
                        ? previousInspectionDate
                        : image.createdAt,
                    ),
                    'MMMM-dd-yyyy',
                  )}
                </div>
              )}
            </Slide>
          ))}
        </Slider>

        <nav
          className={css({
            flexShrink: 0,
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            layout: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '$scale600',
          })}
        >
          <ButtonBack
            className={css({
              pointerEvents: 'auto',
              backgroundColor: '$white !important',
              borderRadius: '$full !important',
              p: '$scale200',
            })}
          >
            <ChevronLeft size={32} />
          </ButtonBack>

          <ButtonNext
            className={css({
              pointerEvents: 'auto',
              backgroundColor: '$white !important',
              borderRadius: '$full !important',
              p: '$scale200',
            })}
          >
            <ChevronRight size={32} />
          </ButtonNext>
        </nav>
        <div
          className={css({
            p: '$scale600',
            position: 'absolute',
            right: 0,
            bottom: 0,
          })}
        >
          <div
            className={css({
              py: '$scale100',
              px: '$scale300',
              backgroundColor: '$mono200',
              borderRadius: '$full',
            })}
          >
            <SlidePosition />
          </div>
        </div>
      </div>
    </CarouselProvider>
  );
}

function DefectInformation({
  defect,
  observation,
  isPreviousDefect,
}: {
  defect: ObservationDefect;
  observation: Observation;
  isPreviousDefect?: boolean;
}) {
  const { css } = useStitches();
  const dictionaryQuery = useDictionary();

  const shownDefect = dictionaryQuery.data?.defects.find(
    (d) => d.id === defect.defectId,
  );
  const component = dictionaryQuery.data?.structuralComponents.find(
    (d) => d.id === observation.structuralComponentId,
  );

  const subcomponent = dictionaryQuery.data?.subComponents.find(
    (d) => d.id === observation.subComponentId,
  );

  return (
    <aside
      className={css({
        overflow: 'hidden',
        divideY: '$1',
        padY: '$scale600',
        padX: '$scale600',
        position: 'relative',
      })}
    >
      <div>
        <div>
          <Text
            variant="LabelXSmall"
            weight="bold"
            css={{
              textTransform: 'uppercase',
              letterSpacing: '$tracking-wide',
              p: '$scale100',
              backgroundColor: '$primary',
              color: '$white',
            }}
          >
            {isPreviousDefect ? 'Previous' : 'Current'} Defect#
          </Text>
          <Text variant="LabelLarge" weight="semibold">
            {defect.id}
          </Text>
          <div className={css({ marginTop: '$scale300' })}>
            <Text variant="LabelSmall">{component?.name}</Text>
            <Text
              variant="LabelSmall"
              weight="semibold"
              css={{ color: '$primary' }}
            >
              {subcomponent?.name}
            </Text>
          </div>
        </div>
      </div>
      <dl
        className={css({
          rowGap: '$scale600',
          display: 'grid',
          gridTemplateColumns: 'repeat(2,1fr)',
        })}
      >
        <div className={css({ spaceY: '$scale100' })}>
          <Text as={'dt'} variant="LabelSmall" css={{ color: '$mono600' }}>
            Inspector
          </Text>
          <dd>
            <Text>
              {defect.createdBy.firstName} {defect.createdBy.lastName}
            </Text>
          </dd>
        </div>
        <div className={css({ spaceY: '$scale100' })}>
          <Text as={'dt'} variant="LabelSmall" css={{ color: '$mono600' }}>
            Inspection Date
          </Text>
          <Text as={'dd'}>{defect?.createdAtClient ? defect.createdAtClient.toLocaleString() : 'No Date'}</Text>

        </div>
        <div className={css({ spaceY: '$scale100' })}>
          <Text as={'dt'} variant="LabelSmall" css={{ color: '$mono600' }}>
            Defect
          </Text>
          <Text as={'dd'}>{shownDefect?.name ?? 'No Defect Name'}</Text>
        </div>
      </dl>
    </aside>
  );
}

function DeleteImage({
  params,
}: {
  params: Omit<UpdateImageParams, 'inspectionId'>;
}) {
  const { css } = useStitches();
  const mutation = useDeleteDefectPhotoMutation();
  const queryClient = useQueryClient();
  const { inspectionId = '' } = useParams<{ inspectionId: string }>();
  const { enqueue } = useSnackbar();

  return (
    <Button
      size="mini"
      isLoading={mutation.isPending}
      className={css({
        backgroundColor: '$negative400 !important',
        '&:hover': {
          color: 'white !important',
          backgroundColor: '$negative500 !important',
        },
      })}
      startEnhancer={DeleteOutlineIcon}
      onClick={() =>
        mutation.mutate(
          { ...params, inspectionId },
          {
            async onSuccess(data, variables, context) {
              await queryClient.invalidateQueries({
                queryKey: [
                  'inspection',
                  variables.inspectionId,
                  'related-cloned-inspections',
                ],
              });
            },
            onError() {
              enqueue({
                message: 'Failed delete image',
              });
            },
          },
        )
      }
    >
      Delete Image
    </Button>
  );
}

